import React from "react";
import type { GroupImperativeHandle, Layout } from "react-resizable-panels";
import { readMailPanelPreference, saveMailPanelPreference,
  type MailPanelPreference } from "./mail-panel-preference";

export const MAIL_PANELS = { list: "mail-list", source: "mail-source", review: "mail-review" } as const;
const SEPARATORS_WIDTH = 24;

export function mailPanelLayout(width: number, preference: MailPanelPreference): Layout {
  const available = Math.max(980, width - SEPARATORS_WIDTH);
  const list = preference.collapsed ? 0 : Math.min(360, available - 740, Math.max(240, preference.listWidth));
  const remaining = available - list;
  const source = Math.min(remaining - 420, Math.max(320, remaining * preference.sourceShare));
  return {
    [MAIL_PANELS.list]: list / available * 100,
    [MAIL_PANELS.source]: source / available * 100,
    [MAIL_PANELS.review]: (remaining - source) / available * 100,
  };
}

/** Only deliberate layout interactions replace the intended desktop size. */
export function useMailPanelLayout(adaptive: boolean, userId: string | null) {
  const groupRef = React.useRef<GroupImperativeHandle>(null);
  const elementRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  // A new identity gets a separate holder even when React retains this workspace.
  // Tenant context and request navigation deliberately do not participate in this key.
  const preference = React.useMemo(() => ({ current: readMailPanelPreference(userId) }), [userId]);
  const [displayed, setDisplayed] = React.useState({ owner: preference, collapsed: preference.current.collapsed });
  const collapsed = displayed.owner === preference ? displayed.collapsed : preference.current.collapsed;
  // Keep initial sizing out of view until the saved layout has been applied.
  // Opacity preserves focus restoration by the parent during history navigation.
  const [restored, setRestored] = React.useState<typeof preference | null>(null);
  // Freeze the non-adjacent panel: the library otherwise cascades a resize past
  // the source minimum and may collapse the list from the review separator.
  const [resizeTarget, setResizeTarget] = React.useState<"list" | "review" | null>(null);

  const apply = React.useCallback(() => {
    const width = elementRef.current?.clientWidth ?? 0;
    if (!adaptive && width > 0 && groupRef.current) {
      groupRef.current.setLayout(mailPanelLayout(width, preference.current));
      setRestored(preference);
    }
  }, [adaptive, preference]);

  React.useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || adaptive) return;
    let previousWidth = 0;
    let frame = 0;
    const resize = () => {
      if (element.clientWidth === previousWidth) return;
      previousWidth = element.clientWidth;
      // Wait for the library's observer to recompute pixel constraints. Observer
      // registration order can change when panel constraints are re-registered.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [adaptive, apply]);

  const onLayoutChange = React.useCallback((layout: Layout) => {
    if (!adaptive) setDisplayed({ owner: preference, collapsed: layout[MAIL_PANELS.list] === 0 });
  }, [adaptive, preference]);

  const onLayoutChanged = React.useCallback((layout: Layout, meta: { isUserInteraction: boolean }) => {
    if (adaptive || !meta.isUserInteraction) return;
    const available = (elementRef.current?.clientWidth ?? 0) - SEPARATORS_WIDTH;
    const list = layout[MAIL_PANELS.list];
    const source = layout[MAIL_PANELS.source];
    const review = layout[MAIL_PANELS.review];
    preference.current = {
      listWidth: list > 0 ? available * list / 100 : preference.current.listWidth,
      sourceShare: source / (source + review),
      collapsed: list === 0,
    };
    saveMailPanelPreference(userId, preference.current);
  }, [adaptive, preference, userId]);

  const toggleList = React.useCallback(() => {
    preference.current.collapsed = !collapsed;
    setDisplayed({ owner: preference, collapsed: !collapsed });
    saveMailPanelPreference(userId, preference.current);
    apply();
  }, [apply, collapsed, preference, userId]);

  const onSeparatorFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target.id === "mail-list-separator") setResizeTarget("list");
    if (event.target.id === "mail-review-separator") setResizeTarget("review");
  };

  return { groupRef, elementRef, listRef, collapsed, toggleList,
    ready: adaptive || restored === preference, onLayoutChange, onLayoutChanged,
    resizeTarget, setResizeTarget, onSeparatorFocus };
}
